// 組織設定 › IT 監控 › 代建的客戶（`/org/kmgr/built`；SPEC #kinio-kms-handover「代建組織」、D37 / D38；k1-wbs M5-03）：
// 任何 team 組織的 owner / org_admin 都能〔替客戶建立組織〕（不算進自己的 5 個；這個組織還沒接手的代建組織最多 20 個）；
// 清單只給名稱、代建者、接手客戶、建立時間——不給進那個組織的權限，完成接手後從清單消失、之後的關係只看委派。
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../../lib/AppContext';
import { useScope } from '../../../lib/scope';
import { orgsApi, type MyOrg } from '../../../lib/planeApi';
import { stamp } from '../../../lib/timefmt';
import { SkeletonLines } from '../../../components/Skeleton';
import { errText, NS, StateBox } from '../parts';

export function BuiltOrgs({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const { planes, setScope } = useScope();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['built-orgs', org.org_id], queryFn: () => orgsApi.builtOrgs(org), retry: 0 });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const admin = org.role === 'owner' || org.role === 'org_admin';
  if (!admin) return <p className="card muted">{t('org.no_admin')}</p>;
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2) return;
    setBusy(true);
    setErr(null);
    try {
      const plane = planes.find((p) => p.api === org.api) ?? planes.find((p) => p.env === config.env) ?? planes[0];
      const r = await orgsApi.create(plane, n, org.org_id);
      setName('');
      // 範圍的組織清單同時看 /v1/me/orgs 與 /v1/me/devices 的 orgs：兩個都重讀，新組織才選得到
      await Promise.all([qc.invalidateQueries({ queryKey: ['me-orgs'] }), qc.invalidateQueries({ queryKey: ['me-devices'] })]);
      void qc.invalidateQueries({ queryKey: ['built-orgs', org.org_id] });
      setScope('org:' + r.org.org_id, '', { announce: t('built.switched', { name: r.org.display_name }) });
      nav('/org/kmgr/handover?org=' + encodeURIComponent(r.org.org_id));
    } catch (x) {
      if (isKinioError(x) && x.code === 'ORG_LIMIT' && x.detail?.limit === 'built_orgs') setErr(t('built.limit', { n: x.detail.max ?? 20 }));
      else setErr(errText(t, x));
    } finally {
      setBusy(false);
    }
  };
  const list = q.data?.built_orgs ?? [];
  return (
    <div data-testid="km-built">
      <section className="card" style={{ marginBottom: 'var(--space-3)' }} aria-labelledby="km-built-new-h">
        <h2 id="km-built-new-h" style={{ fontSize: 16, marginTop: 0 }}>
          {t('built.new_title')}
        </h2>
        <p className="muted km-sub">{t('built.new_body', { org: org.display_name })}</p>
        <form className="toolbar wrap" onSubmit={(e) => void create(e)} style={{ margin: 0 }}>
          <label className="km-sr" htmlFor="km-built-name">
            {t('built.name')}
          </label>
          <input id="km-built-name" className="input" required minLength={2} maxLength={60} value={name} placeholder={t('built.name')} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-primary" disabled={busy || name.trim().length < 2} data-testid="km-built-create">
            {busy ? t('common.saving') : t('built.create')}
          </button>
        </form>
        {err && (
          <p className="error" role="alert" data-testid="km-built-err">
            {err}
          </p>
        )}
      </section>
      {q.isLoading ? (
        <SkeletonLines n={3} label={t('common.loading')} />
      ) : q.isError ? (
        <StateBox icon="⊘" title={t('built.unavailable')} body={errText(t, q.error)} />
      ) : !list.length ? (
        <StateBox icon="○" title={t('built.none')} body={t('built.none_body')} testId="km-built-empty" />
      ) : (
        <div className="card">
          <p className="muted km-sub">{t('built.count', { n: q.data?.count ?? list.length, max: q.data?.max ?? 20 })}</p>
          <table className="km-table" data-testid="km-built-list">
            <thead>
              <tr>
                <th>{t('built.col_name')}</th>
                <th>{t('built.col_builder')}</th>
                <th>{t('built.col_customer')}</th>
                <th>{t('built.col_created')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.org_id} data-org={b.org_id}>
                  <td>{b.display_name}</td>
                  <td>{b.builder.name || b.builder.user_id}</td>
                  <td>
                    {b.customer
                      ? t(b.owner_transferred ? 'built.customer_owner' : 'built.customer_joined', { name: b.customer.name || b.customer.user_id })
                      : b.invite_email
                        ? t('built.customer_invited', { email: b.invite_email })
                        : t('built.customer_none')}
                  </td>
                  <td className="tabular">{stamp(b.created_at).slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
